import {
  et3ParseInteger,
  isDefinedString,
  isPositiveNumber,
  reduce
} from "et3-type-guards";
import { memoize } from "lodash";
import { computed, observable, runInAction } from "mobx";

import { apiCallGetTransferUserPackagePreview } from "../../../api/developer-api/api-call-get-transfer-user-package-preview";
import { apiCallTransferUserPackage } from "../../../api/developer-api/api-call-transfer-user-package";
import {
  DEFAULT_TRANSFER_USER_PACKAGE_PREVIEW,
  EMPTY_USER_ID
} from "../../../constants/input-entries-constants";
import { DEFAULT_ET3_PICKER_SINGLE_VALUE } from "../../../constants/picker-entries-constants";
import { getReadableDateFromUnixMilliseconds } from "../../../helpers/et3-date-helper";
import {
  fetchUserGender,
  isGenderedPackage,
  isMaleOnlyPackage,
  isTransferPackageType
} from "../../../helpers/et3-transfer-package-helper";
import {
  Gender,
  StringOrNumber,
  TTransferPackageType,
  TTransferUserPackagePreview
} from "../../../Types";
import { TEt3PickerSingleValue } from "../../../TypesNEW";
import getCorePopUpStore from "../../core/core-popup-store";
import getCoreUIStore from "../../core/core-ui-store";

class TransferUserPackageStore {
  fromUserId = observable.box<number>(EMPTY_USER_ID);
  toUserId = observable.box<number>(EMPTY_USER_ID);
  packageType = observable.box<TEt3PickerSingleValue>(
    DEFAULT_ET3_PICKER_SINGLE_VALUE
  );
  isTransferInFlight = observable.box<boolean>(false);
  isGenderCheckInFlight = observable.box<boolean>(false);
  isPreviewInFlight = observable.box<boolean>(false);
  preview = observable.box<TTransferUserPackagePreview>(
    DEFAULT_TRANSFER_USER_PACKAGE_PREVIEW
  );

  selectedPackageType = computed<string>(() =>
    reduce(() => this.packageType.get().value, "")
  );

  areUserIdsValid = computed<boolean>(() => {
    const fromUserId = this.fromUserId.get();
    const toUserId = this.toUserId.get();
    return (
      isPositiveNumber(fromUserId) &&
      isPositiveNumber(toUserId) &&
      fromUserId !== toUserId
    );
  });

  isTransferEnabled = computed<boolean>(
    () =>
      this.areUserIdsValid.get() &&
      isDefinedString(this.selectedPackageType.get()) &&
      !this.isTransferInFlight.get() &&
      !this.isGenderCheckInFlight.get() &&
      !this.isPreviewInFlight.get()
  );

  setFromUserId = (value: StringOrNumber) => {
    runInAction(() => {
      this.fromUserId.set(et3ParseInteger(value));
    });
  };

  setToUserId = (value: StringOrNumber) => {
    runInAction(() => {
      this.toUserId.set(et3ParseInteger(value));
    });
  };

  setPackageType = (value: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.packageType.set(value);
    });
  };

  resetForm = () => {
    runInAction(() => {
      this.fromUserId.set(EMPTY_USER_ID);
      this.toUserId.set(EMPTY_USER_ID);
      this.packageType.set(DEFAULT_ET3_PICKER_SINGLE_VALUE);
      this.preview.set(DEFAULT_TRANSFER_USER_PACKAGE_PREVIEW);
    });
  };

  // Fast-fails the gendered packages before the admin ever sees the confirmation.
  // The server remains the authority via errors 3115 and 3101.
  private isGenderPairAllowed = async (
    packageType: TTransferPackageType
  ): Promise<boolean> => {
    const [fromLookup, toLookup] = await Promise.all([
      fetchUserGender(this.fromUserId.get()),
      fetchUserGender(this.toUserId.get())
    ]);

    // A missing user must read as "user not found", the same as the server's
    // 3016 — not as a user whose gender happens to be unreadable.
    if (!fromLookup.isFound || !toLookup.isFound) {
      getCorePopUpStore().showAlertMessage("USER_NOT_FOUND");
      return false;
    }

    if (
      !isDefinedString(fromLookup.gender) ||
      !isDefinedString(toLookup.gender)
    ) {
      getCorePopUpStore().showAlertMessage("TRANSFER_PACKAGE_UNKNOWN_GENDER");
      return false;
    }

    if (isMaleOnlyPackage(packageType)) {
      if (
        fromLookup.gender !== Gender.Male ||
        toLookup.gender !== Gender.Male
      ) {
        getCorePopUpStore().showAlertMessage("USER_IS_NOT_MALE_USER");
        return false;
      }
      return true;
    }

    if (fromLookup.gender !== toLookup.gender) {
      getCorePopUpStore().showAlertMessage("TRANSFER_PACKAGE_GENDER_MISMATCH");
      return false;
    }
    return true;
  };

  openTransferConfirmation = async () => {
    if (!this.isTransferEnabled.get()) {
      return;
    }
    const packageType = this.selectedPackageType.get();
    if (!isTransferPackageType(packageType)) {
      return;
    }

    if (isGenderedPackage(packageType)) {
      runInAction(() => {
        this.isGenderCheckInFlight.set(true);
      });
      let isAllowed = false;
      try {
        isAllowed = await this.isGenderPairAllowed(packageType);
      } finally {
        // Without finally a throw here would leave the form permanently
        // disabled, with no way back short of a reload.
        runInAction(() => {
          this.isGenderCheckInFlight.set(false);
        });
      }
      if (!isAllowed) {
        return;
      }
    }

    const isPreviewLoaded = await this.loadPreview(packageType);
    if (!isPreviewLoaded) {
      return;
    }

    getCorePopUpStore().openConfirmationPopUps("TRANSFER_USER_PACKAGE_POPUP");
  };

  private loadPreview = async (
    packageType: TTransferPackageType
  ): Promise<boolean> => {
    runInAction(() => {
      this.isPreviewInFlight.set(true);
    });
    try {
      const { status, data, code } = await apiCallGetTransferUserPackagePreview(
        {
          fromUserId: this.fromUserId.get(),
          toUserId: this.toUserId.get(),
          type: packageType
        }
      );

      if (status === "ERROR") {
        runInAction(() => {
          this.preview.set(DEFAULT_TRANSFER_USER_PACKAGE_PREVIEW);
        });
        getCorePopUpStore().showAlertMessageWithExtraData(code, packageType);
        return false;
      }

      runInAction(() => {
        this.preview.set(data);
      });
      return true;
    } finally {
      runInAction(() => {
        this.isPreviewInFlight.set(false);
      });
    }
  };

  transferUserPackage = async () => {
    // The button's isEnabled covers the form, but the confirmation popup's
    // confirm icon is a bare FontAwesomeIcon with no disabled state, so the
    // store has to guard itself against a double-click firing two transfers.
    if (this.isTransferInFlight.get() || !this.isTransferEnabled.get()) {
      return;
    }
    const packageType = this.selectedPackageType.get();
    if (!isTransferPackageType(packageType)) {
      return;
    }

    // Nothing may await between reading isTransferInFlight above and setting it
    // here, or a double-click could slip through and fire two transfers.
    runInAction(() => {
      this.isTransferInFlight.set(true);
    });
    getCoreUIStore().setIsLoading("DEVELOPER_TRANSFER_USER_PACKAGE", true);
    getCorePopUpStore().closeConfirmationPopUps();

    try {
      const { status, data, code } = await apiCallTransferUserPackage({
        fromUserId: this.fromUserId.get(),
        toUserId: this.toUserId.get(),
        type: packageType
      });

      if (status === "ERROR") {
        // Only the two package-specific keys are registered in
        // messageWithExtraData, so the rest render plain through this same call.
        getCorePopUpStore().showAlertMessageWithExtraData(code, packageType);
        return;
      }

      getCorePopUpStore().showSuccessMessageWithArrayExtraData(
        "TRANSFER_USER_PACKAGE_SUCCESS",
        [
          reduce(() => data.package, ""),
          `${reduce(() => data.fromUserId, EMPTY_USER_ID)}`,
          `${reduce(() => data.toUserId, EMPTY_USER_ID)}`,
          getReadableDateFromUnixMilliseconds(reduce(() => data.newExpiry, 0))
        ],
        false
      );
      this.resetForm();
    } finally {
      // The transfer is irreversible; a throw must not leave the page wedged
      // with a spinner and a permanently disabled button.
      getCoreUIStore().setIsLoading("DEVELOPER_TRANSFER_USER_PACKAGE", false);
      runInAction(() => {
        this.isTransferInFlight.set(false);
      });
    }
  };
}

const getTransferUserPackageStore = memoize(
  () => new TransferUserPackageStore(),
  () => 1
);
export default getTransferUserPackageStore;
